import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Select, type SelectProps } from 'antd';
import { fn } from '@app/utils';
import type { RangePickerProps } from 'antd/es/date-picker';
const { RangePicker } = DatePicker;

const DEFAULT_CUSTOMDATERANGE = {
    open: false,
    dates: null as RangePickerProps['value'],
    dateString: [] as string[],
};

export type DateRangeSelectProps = SelectProps & {
    initialValue?: number;
    onSelectionChange?: (params: { start: string; end: string }) => void;
    allowCustomRange?: boolean;
    totalMonthOptions?: number;
    dynamicMonthOptions?: boolean;
};

const defaultOptions = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];

const createMonthOptions = (num: number) => {
    const now = dayjs();
    const options = [];
    for (let i = 0; i < num; i++) {
        options.push({
            value: i + 1,
            label: now.subtract(i, 'month').format("MMM 'YY"),
        });
    }
    return options;
};

export const DateRangeSelect = ({
    totalMonthOptions = 3,
    dynamicMonthOptions = false,
    options = dynamicMonthOptions ? createMonthOptions(totalMonthOptions) : defaultOptions,
    initialValue = dynamicMonthOptions ? 1 : 30,
    allowCustomRange = true,
    onSelectionChange = () => {},
    ...props
}: DateRangeSelectProps) => {
    const [days, setDays] = useState<number>(dynamicMonthOptions ? 1 : initialValue);
    const [customDateRange, setCustomDateRange] = useState(DEFAULT_CUSTOMDATERANGE);
    const customDateRanges = customDateRange.dateString;
    const customDateRangesSet = customDateRanges.length || customDateRange.open;

    const params = useMemo(() => {
        if (customDateRanges?.length) {
            let start = customDateRanges[0];
            let end = customDateRanges[1];
            if (dynamicMonthOptions) {
                start = `${start} 00:00:00`;
                end = `${end} 23:59:59`;
            }
            return { start: start, end: end };
        }
        if (dynamicMonthOptions) {
            const now = dayjs();
            const startDate = now
                .subtract(days - 1, 'month')
                .startOf('month')
                .format('YYYY-MM-DD');
            const endDate = now
                .subtract(days - 1, 'month')
                .endOf('month')
                .format('YYYY-MM-DD');
            return { start: `${startDate} 00:00:00`, end: `${endDate} 23:59:59` };
        }

        const today = new Date();
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const start = new Date(end);
        start.setDate(end.getDate() - (days - 1));
        return { start: fn.formateDateToYMD(start), end: fn.formateDateToYMD(end) };
    }, [days, customDateRanges, dynamicMonthOptions]);

    useEffect(() => {
        onSelectionChange(params);
    }, [params, onSelectionChange]);

    const getOptions = () => {
        if (!allowCustomRange) {
            return options;
        }

        return [
            ...options,
            {
                value: -1,
                label: (
                    <div
                        style={{
                            lineHeight: customDateRange.dates?.length ? '15px' : 'inherit',
                        }}>
                        <div>Custom Range</div>
                        {customDateRanges?.length ? (
                            <small style={{ fontSize: 10, lineHeight: 1 }}>
                                {[
                                    fn.formatDate(customDateRanges[0]),
                                    fn.formatDate(customDateRanges[1]),
                                ].join(' - ')}
                            </small>
                        ) : null}
                    </div>
                ),
            },
        ];
    };

    return (
        <div>
            <Select
                {...props}
                prefix={<CalendarOutlined style={{ marginRight: 5 }} />}
                options={getOptions()}
                value={customDateRangesSet ? -1 : days}
                onChange={(value) => {
                    if (value > 0) {
                        setDays(Number(value));
                        setCustomDateRange(DEFAULT_CUSTOMDATERANGE);
                    }
                }}
                onSelect={(value) => {
                    if (value === -1) {
                        const startInput = document.getElementById('startInput');
                        startInput?.click();
                    }
                }}
                allowClear={Boolean(customDateRanges?.length)}
                onClear={() => {
                    setCustomDateRange(DEFAULT_CUSTOMDATERANGE);
                }}
            />

            {allowCustomRange ? (
                <div style={{ position: 'relative' }}>
                    <RangePicker
                        style={{ position: 'absolute', bottom: 0, zIndex: -1 }}
                        value={customDateRange.dates}
                        id={{
                            start: 'startInput',
                            end: 'endInput',
                        }}
                        maxDate={dayjs()}
                        onOpenChange={(open) => setCustomDateRange((prev) => ({ ...prev, open }))}
                        onChange={(dates, dateString) => {
                            setCustomDateRange((prev) => ({
                                ...prev,
                                dates,
                                dateString,
                            }));
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
};
